import { HeaderIcons } from '../../shared/icons';
import React, { useState, useEffect, useRef, useMemo } from 'react';
import Header from '../../shared/Header';
import { StandingsSidebar, TimerSection, WaitingSpinner } from '../../shared';
import { BonusLevelHost } from './BonusLevelHost';
import { BonusWinnerRoll } from './BonusWinnerRoll';
import {
  BonusLevelPayload,
  BonusGameType,
  BONUS_INTRO_MS,
  BONUS_SUBMIT_ONCE_GAME_TYPES,
} from '../../../constants/bonusConstants';
import { getBonusNoWinnerMessages } from '../../../i18n/helpers';
import { LiveAnswer } from '../../shared/LiveAnswers';
import { getGameName } from '../../../utils/gameUtils';
import { syncPlayersFromLeaderboard } from '../../../utils/leaderboardUtils';
import { playBonusWinnerSound } from '../../../utils/sounds';
import '../../../styles/game-layout.css';
import { getRandomPuzzleImageUrl } from '../../../utils/bonusPuzzleImage';
import { useTranslation } from '../../../i18n';

interface BonusHostScreenProps {
  roomState: {
    roomName: string;
    gameType: string;
    hostId: string;
    players: Array<{ id: string; name: string; score: number; avatar?: string }>;
    gameState: string;
    isBonusLevel?: boolean;
    bonusLevelPayload?: BonusLevelPayload | null;
    bonusStartTime?: number;
    settings?: { timerDuration?: number };
  };
  socket: { on: (e: string, cb: (d: any) => void) => void; off: (e: string, cb: (d: any) => void) => void; emit: (event: string, data: any) => void };
  onBackToGameSelection?: () => void;
  onGoHome?: () => void;
}

/**
 * Full host screen during bonus: same layout as GameDisplay (timer + left column + StandingsSidebar).
 * Left column = BonusLevelHost (intro -> game view -> results).
 */
export const BonusHostScreen: React.FC<BonusHostScreenProps> = ({
  roomState,
  socket,
  onBackToGameSelection,
  onGoHome,
}) => {
  const { t } = useTranslation();
  const payload = roomState.bonusLevelPayload ?? null;
  const bonusStartTime = roomState.bonusStartTime ?? Date.now();
  const timeLimit = payload?.timeLimit ?? 30000;
  
  const [phase, setPhase] = useState<'intro' | 'playing' | 'results'>('intro');
  const [liveAnswers, setLiveAnswers] = useState<LiveAnswer[]>([]);
  const [timeRemaining, setTimeRemaining] = useState(timeLimit);
  /** Points per player from bonus-level-result (for green +N display); cleared on new bonus. */
  const [bonusResultPoints, setBonusResultPoints] = useState<Map<string, number>>(new Map());
  /** Winner of this bonus round (top scorer; updated on bonus-points-updated for late submits) */
  const [bonusWinner, setBonusWinner] = useState<{ playerName: string; points: number } | null>(null);
  /** True only after bonus-level-result — avoids "no winner" flash during grace before server totals */
  const [bonusOutcomeReady, setBonusOutcomeReady] = useState(false);
  /** Leaderboard from bonus-level-result (authoritative scores; avoids delay until room-state arrives) */
  const [bonusLeaderboard, setBonusLeaderboard] = useState<Array<{ playerId: string; playerName: string; score: number }> | null>(null);
  /** Keep payload cached so we can show results even after backend clears it */
  const [cachedPayload, setCachedPayload] = useState<BonusLevelPayload | null>(payload);
  const questionIdRef = useRef<string | undefined>(payload?.questionId);
  /** For puzzle bonus: pick local image once during intro and broadcast to room */
  const puzzleImageFetchedRef = useRef<string | undefined>(undefined);

  // Use cached payload if current payload is null (happens when backend clears bonus)
  const displayPayload = payload ?? cachedPayload;

  // Sync payload and reset phase when bonus changes
  useEffect(() => {
    if (payload?.questionId !== questionIdRef.current) {
      questionIdRef.current = payload?.questionId;
      puzzleImageFetchedRef.current = undefined;
      setCachedPayload(payload);
      setPhase('intro');
      setLiveAnswers([]);
      setBonusResultPoints(new Map());
      setBonusWinner(null);
      setBonusOutcomeReady(false);
      setBonusLeaderboard(null);
      setTimeRemaining(payload?.timeLimit ?? 30000);
    } else if (payload) {
      // Update cached payload if it exists
      setCachedPayload(payload);
    }
  }, [payload?.questionId, payload?.timeLimit, payload]);

  // Puzzle bonus: during intro, pick a random local image and broadcast to room (no API)
  useEffect(() => {
    if (phase !== 'intro' || displayPayload?.bonusGameType !== 'puzzle' || !displayPayload?.questionId) return;
    if (puzzleImageFetchedRef.current === displayPayload.questionId) return;

    puzzleImageFetchedRef.current = displayPayload.questionId;
    const imageUrl = getRandomPuzzleImageUrl();
    if (roomState.roomName && socket?.emit) {
      socket.emit('bonus-puzzle-set-image', { roomName: roomState.roomName, imageUrl });
    }
  }, [phase, displayPayload?.bonusGameType, displayPayload?.questionId, roomState.roomName, socket]);

  // Timer: during intro, show full bar (no countdown). After intro, count down from timeLimit.
  // Backend timer runs for timeLimit + BONUS_INTRO_MS so round ends after intro + timeLimit.
  const totalPlayers = roomState.players.length;
  /** Only submit-once bonuses (server ends when everyone submitted); incremental/timer games never use this shortcut. */
  const bonusType = displayPayload?.bonusGameType as BonusGameType | undefined;
  const isSubmitOnceBonus =
    bonusType != null && BONUS_SUBMIT_ONCE_GAME_TYPES.includes(bonusType);
  const allAnswered =
    isSubmitOnceBonus && totalPlayers > 0 && liveAnswers.length >= totalPlayers;
  const roundEnded = allAnswered || timeRemaining <= 0;

  useEffect(() => {
    if (allAnswered) {
      setTimeRemaining(0);
      setPhase('results'); // Change to results when all answered
      return;
    }
    if (!payload || phase === 'results') return;
    const roundEndTime = bonusStartTime + BONUS_INTRO_MS + timeLimit;
    const interval = setInterval(() => {
      if (phase === 'intro') {
        // Freeze at full during intro (show full game time)
        setTimeRemaining(timeLimit);
        return;
      }
      // Same formula as player: remaining until round end; when playing this equals game time left
      const remaining = Math.max(0, roundEndTime - Date.now());
      setTimeRemaining(Math.min(timeLimit, remaining));
      if (remaining <= 0) {
        setPhase('results'); // for sidebar shouldUpdateScores when bonus-level-result arrives
      }
    }, 200);
    return () => clearInterval(interval);
  }, [payload, bonusStartTime, timeLimit, phase, allAnswered]);

  const timeSeconds = Math.ceil(timeRemaining / 1000);
  const progress = timeLimit > 0 ? (timeRemaining / timeLimit) * 100 : 0;

  // Listen for bonus-level-start and bonus-level-result
  useEffect(() => {
    const onStart = (data: BonusLevelPayload) => {
      setPhase('intro');
      setLiveAnswers([]);
      setBonusResultPoints(new Map());
      setBonusWinner(null);
      setBonusOutcomeReady(false);
      setBonusLeaderboard(null);
    };
    const onTimeUp = () => {
      setPhase('results');
      setTimeRemaining(0); // Game time over, collecting submissions during grace
    };
    const onResult = (data: {
      submissions?: Array<{ playerId: string; playerName: string; points: number }>;
      leaderboard?: Array<{ playerId: string; playerName: string; score: number }>;
    }) => {
      setPhase('results');
      setTimeRemaining(0); // Stop timer display when server sends final results
      if (data?.submissions?.length) {
        const map = new Map<string, number>();
        data.submissions.forEach((s) => map.set(s.playerId, s.points));
        setBonusResultPoints(map);
        const top = data.submissions[0];
        if (top && top.points > 0) {
          setBonusWinner({ playerName: top.playerName, points: top.points });
          playBonusWinnerSound();
        } else {
          setBonusWinner(null);
        }
      } else {
        setBonusResultPoints(new Map());
        setBonusWinner(null);
      }
      if (data?.leaderboard?.length) {
        setBonusLeaderboard(data.leaderboard);
      }
      setBonusOutcomeReady(true);
    };
    const onPointsUpdated = (data: {
      submissions?: Array<{ playerId: string; playerName: string; points: number }>;
      leaderboard?: Array<{ playerId: string; playerName: string; score: number }>;
    }) => {
      if (data?.submissions?.length) {
        const map = new Map<string, number>();
        data.submissions.forEach((s) => map.set(s.playerId, s.points));
        setBonusResultPoints(map);
        const top = data.submissions[0];
        if (top && top.points > 0) {
          setBonusWinner({ playerName: top.playerName, points: top.points });
        } else {
          setBonusWinner(null);
        }
      }
      if (data?.leaderboard?.length) setBonusLeaderboard(data.leaderboard);
    };
    const onAnswered = (data: { playerId: string; playerName: string; avatar?: string; answer: string | number; timestamp?: number; questionId?: string }) => {
      setLiveAnswers((prev) => {
        // Only count answers for the current bonus round (ignore stale/trivia questionId)
        const currentBonusQuestionId = displayPayload?.questionId;
        if (currentBonusQuestionId && data.questionId != null && data.questionId !== currentBonusQuestionId) {
          return prev;
        }
        const next = prev.filter((a) => a.playerId !== data.playerId);
        next.push({
          playerId: data.playerId,
          playerName: data.playerName,
          avatar: data.avatar,
          answer: typeof data.answer === 'number' ? String(data.answer) : data.answer,
          timestamp: data.timestamp ?? Date.now(),
        });
        return next;
      });
    };
    socket.on('bonus-level-start', onStart);
    socket.on('bonus-time-up', onTimeUp);
    socket.on('bonus-level-result', onResult);
    socket.on('bonus-points-updated', onPointsUpdated);
    socket.on('player-answered', onAnswered);
    return () => {
      socket.off('bonus-level-start', onStart);
      socket.off('bonus-time-up', onTimeUp);
      socket.off('bonus-level-result', onResult);
      socket.off('bonus-points-updated', onPointsUpdated);
      socket.off('player-answered', onAnswered);
    };
  }, [socket, displayPayload?.questionId]);

  const questionNumber = displayPayload?.questionNumber ?? 0;
  const totalQuestions = displayPayload?.totalQuestions ?? 0;

  /** One random "no winner" message per bonus round (stable for the round). */
  const noWinnerMessage = useMemo(
    () => getBonusNoWinnerMessages()[Math.floor(Math.random() * getBonusNoWinnerMessages().length)],
    [displayPayload?.questionId]
  );

  // Use leaderboard from bonus-level-result when available so totals show immediately (no waiting
  // for room-state). Merge avatars from roomState.players.
  const standingsPlayers = useMemo(() => {
    if (!bonusLeaderboard?.length) return roomState.players;
    return syncPlayersFromLeaderboard(roomState.players, bonusLeaderboard);
  }, [bonusLeaderboard, roomState.players]);

  const winnerNamePool = useMemo(() => standingsPlayers.map((p) => p.name), [standingsPlayers]);

  const handleRestartGame = () => {
    if (socket?.emit && roomState?.roomName) {
      socket.emit('start-game', { roomName: roomState.roomName });
    }
  };

  return (
    <div className="app" data-testid="bonus-host-screen">
      <Header
        leftButton={{
          icon: HeaderIcons.back,
          onClick: onBackToGameSelection ?? (() => {}),
          tooltip: t('gameplay.backToGameSelection'),
        }}
        rightButton={{
          icon: HeaderIcons.refresh,
          onClick: handleRestartGame,
          tooltip: t('gameplay.restartGame'),
        }}
        centerContent={
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1.5rem', flexWrap: 'wrap' }}>
            <div className="header-game-name" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', fontWeight: 600 }}>{t('gameplay.partyHost.gameLabel')}</span>
              <span style={{ fontSize: '1.1rem', fontWeight: 'bold', color: 'var(--text-primary)' }}>
                {getGameName(roomState.gameType ?? '')}
              </span>
            </div>
            <div className="header-game-name" style={{ width: '1px', height: '24px', backgroundColor: 'var(--border-medium)' }}></div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', fontWeight: 600 }}>{t('gameplay.partyHost.roomCodeLabel')}</span>
              <div style={{
                fontSize: '1.3rem',
                fontWeight: 'bold',
                letterSpacing: '0.15rem',
                color: 'var(--color-primary)',
                padding: '0.3rem 0.8rem',
                backgroundColor: 'rgba(var(--color-primary-rgb), 0.1)',
                borderRadius: '6px',
                border: '2px solid var(--color-primary)',
              }}>
                {roomState.roomName}
              </div>
            </div>
          </div>
        }
      />
      <div className="game-host-layout" style={{ padding: '1rem' }}>
        <div className="game-host-left">
          <div className="game-host-timer-section">
            <TimerSection
              questionNumber={questionNumber}
              totalQuestions={totalQuestions}
              timeSeconds={timeSeconds}
              progress={progress}
            />
          </div>
          <div
            className="game-host-question-section"
            style={{
              flex: '1 1 auto',
              display: 'flex',
              flexDirection: 'column',
              // flex-start from the start for puzzle and whos-on-image so title/image don't jump and space stays below for winner
              justifyContent: (phase === 'intro' || phase === 'results' || payload?.bonusGameType === 'puzzle' || payload?.bonusGameType === 'whos-on-image') ? 'flex-start' : 'center',
              alignItems: 'center',
            }}
          >
            <BonusLevelHost payload={displayPayload} phase={phase} onPhaseChange={setPhase} allAnswered={allAnswered} roundEnded={roundEnded} />
            {/* Results: until bonus-level-result, tallying (not "no winner"). Then winner reel or real no-winner copy. */}
            {phase === 'results' && (
              <div
                data-testid="bonus-host-results"
                style={{
                  marginTop: 'auto',
                  paddingBottom: '1.5rem',
                  textAlign: 'center',
                  animation: 'fadeIn 0.5s ease-out',
                  width: '100%',
                }}
              >
                {!bonusOutcomeReady ? (
                  <WaitingSpinner isLoading size="small" message={t('bonus.host.tallyingResults')} />
                ) : bonusWinner ? (
                  <BonusWinnerRoll
                    key={displayPayload?.questionId ?? 'bonus'}
                    target={{ playerName: bonusWinner.playerName, points: bonusWinner.points }}
                    namePool={winnerNamePool}
                  />
                ) : (
                  <>
                    <div className="bonus-host-results-title" style={{ fontSize: 'clamp(1.8rem, 4.5vw, 2.5rem)', marginBottom: '0.5rem', fontWeight: 600 }}>
                      {noWinnerMessage.title}
                    </div>
                    <div className="bonus-host-results-subtitle" style={{ fontSize: 'clamp(1.4rem, 3.5vw, 1.8rem)' }}>
                      {noWinnerMessage.subtitle}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="game-host-right">
          <StandingsSidebar
            players={standingsPlayers}
            liveAnswers={liveAnswers}
            totalPlayers={roomState.players.length}
            questionId={displayPayload?.questionId}
            shouldUpdateScores={phase === 'results' && bonusResultPoints.size > 0}
            pointsGainedOverride={bonusResultPoints.size > 0 ? bonusResultPoints : undefined}
            theme={{
              primary: 'var(--color-primary)',
              primaryLight: 'rgba(var(--color-primary-rgb), 0.15)',
              accent: '#F59E0B',
            }}
          />
        </div>
      </div>
    </div>
  );
};
